// #2372 / WO-2267-UIF -- display-only cleanup of messy legacy names ("Mr. Herron", "B Helms", first "Mr" + last "Shannon Guillot").
// Never written back to contacts. api/_lib/contactName.js is an identical copy for the serverless functions
// (a test keeps the two in sync).
const HONORIFICS = new Set(['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'sir', 'rev']);
const bare = (t) => String(t || '').trim().replace(/\.$/, '');
const isHonorific = (t) => HONORIFICS.has(bare(t).toLowerCase());
const isInitial = (t) => /^[A-Za-z]$/.test(bare(t));

// #2707: a stored name that is ALL UPPER or all lower ("PATRICIO", "patricio") is shown in proper case in an email. A mixed-case name (McDonald, DeShawn, O'Brien, de la Cruz)
// is a real spelling and is never touched. Display only: nothing is written back to the contact.
const NAME_PARTICLES = new Set(['de', 'la', 'del', 'van', 'von', 'der', 'den', 'di', 'da', 'du', 'le', 'bin', 'al', 'el']);
const NAME_SUFFIXES = new Set(['ii', 'iii', 'iv', 'v']);
function caseWord(w, isFirstWord) {
  const lower = w.toLowerCase();
  if (!isFirstWord && NAME_PARTICLES.has(lower)) return lower;
  if (NAME_SUFFIXES.has(lower.replace(/\.$/, ''))) return w.toUpperCase();
  return lower.replace(/(^|[-'\u2019.])([a-z])/g, (m, p, ch) => p + ch.toUpperCase()).replace(/^Mc([a-z]{3,})/, (m, rest) => 'Mc' + rest.charAt(0).toUpperCase() + rest.slice(1));
}
export function properCaseName(value) {
  const s = String(value == null ? '' : value);
  if (!/[A-Za-z]/.test(s) || s.includes('@')) return s; // an email typed into the name field is left exactly as stored
  if (/^[A-Z]{1,2}$/.test(s.trim())) return s; // TJ, AJ, JP: a short all-caps name is a nickname or initials, not a shouted name
  if (s !== s.toUpperCase() && s !== s.toLowerCase()) return s;
  return s.split(/(\s+)/).map((part, i, arr) => (/^\s+$/.test(part) || !part ? part : caseWord(part, arr.slice(0, i).every((x) => /^\s*$/.test(x))))).join('');
}

// Returns { first, last, display, greeting }:
//   display  -- what to show on a card ("Shannon Guillot", "Mr. Herron", "B. Helms")
//   greeting -- what a greeting may say after "Hello": a real first name, or "there" when we do not have one
//               (never "Mr." and never a lone initial).
export function cleanName(firstRaw, lastRaw) {
  let first = String(firstRaw || '').trim();
  let last = String(lastRaw || '').trim();
  let title = '';

  const ft = first.split(/\s+/).filter(Boolean);
  if (ft.length && isHonorific(ft[0])) { title = `${bare(ft[0])}.`; ft.shift(); first = ft.join(' '); }
  if (!first && last) {
    // Honorific-only first name: a multi-word last name carries the real name ("Mr" + "Shannon Guillot").
    const lt = last.split(/\s+/);
    if (lt.length > 1) { first = lt[0]; last = lt.slice(1).join(' '); title = ''; }
  }

  let greeting = properCaseName(first);
  if (isInitial(first)) greeting = '';

  // #2719: what is SHOWN is proper case too ("PATRICIO MARQUEZ" -> "Patricio Marquez"); display only, the stored name is never rewritten
  const shownFirst = isInitial(first) ? `${bare(first).toUpperCase()}.` : properCaseName(first);
  const shownLast = properCaseName(last);
  const display = [title && !first ? title : '', shownFirst, shownLast].filter(Boolean).join(' ').trim();
  return { first: properCaseName(first), last: shownLast, display: display || 'Unnamed contact', greeting: greeting || 'there' };
}

// A person's full name as shown on screen: ALL CAPS or all lower reads in proper case, mixed case is kept. "" when there is no name (callers fall back to the email).
export const personName = (c) => [properCaseName(c && c.first_name), properCaseName(c && c.last_name)].map((x) => String(x || '').trim()).filter(Boolean).join(' ');
